import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, inject, ViewEncapsulation } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { catchError, of } from "rxjs";
import { MaterialModule } from "src/app/material.module";
import { CoreService } from "src/app/services/core.service";

import { Router } from "@angular/router";
import { ProfileService } from "../profile/profile.service";
import { StoreService } from "src/app/services/apps/store/store.service";
import { getOnboardingStore } from "src/app/core/guards/first-time-user-experience.helper";
import { AfterCompleteDeliveryAndThresholdFormBodyComponent } from "./bodyComponents/after-complete-delivery-and-threshold-form-body.component";
import { WelcomeFirstTimeOnboardingBodyComponent } from "./bodyComponents/welcome-first-time-onboarding-body.component";
import { ResumeIncompleteStoreSetupBodyComponent } from "./bodyComponents/resume-incomplete-store-setup-body.component";
import { StoreLinkAfterLinkingBodyComponent } from "./bodyComponents/store-link-after-linking-body.component";
import { DelegatedWelcomeBodyComponent } from "./bodyComponents/delegated-welcome-body.component";
import { describeModalConfirmed } from "src/app/shared/helpers/support-event-descriptions.helper";
import {
  OnboardingModalData,
  OnboardingModalService,
} from "./onboarding-modal.service";

@Component({
  selector: "app-onboarding-modal",
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    TranslateModule,
    MaterialModule,
    WelcomeFirstTimeOnboardingBodyComponent,
    ResumeIncompleteStoreSetupBodyComponent,

    StoreLinkAfterLinkingBodyComponent,
    AfterCompleteDeliveryAndThresholdFormBodyComponent,
    DelegatedWelcomeBodyComponent,
  ],
  templateUrl: "./onboarding-modal.component.html",
  styleUrls: ["./onboarding-modal.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class OnboardingModalComponent {
  readonly router = inject(Router);
  readonly dialogRef = inject(MatDialogRef<OnboardingModalComponent>);
  readonly dialogData = inject(MAT_DIALOG_DATA) as Partial<OnboardingModalData>;
  readonly translate = inject(TranslateService);
  readonly settings = inject(CoreService);
  private readonly cd = inject(ChangeDetectorRef);
  readonly onboardingModalService = inject(OnboardingModalService);
  readonly profileService = inject(ProfileService);
  readonly storeService = inject(StoreService);
  public readonly momentStep =
    this.dialogData?.momentStep ?? "welcomeFirstTimeOnboarding";

  public languages = [
    {
      language: "English",
      code: "en",
      icon: "/assets/images/flag/icon-flag-en.svg",
    },
    {
      language: "Español",
      code: "es",
      icon: "/assets/images/flag/icon-flag-es.svg",
    },
  ];

  public selectedLanguage =
    this.languages.find((lang) => lang.code === this.settings.getLanguage()) ||
    this.languages[0];

  get userName(): string {
    const fullName = this.profileService.userProfile()?.name?.trim();
    if (!fullName) {
      return "there";
    }

    return fullName.split(/\s+/)[0];
  }

  changeLanguage(lang: { language: string; code: string; icon: string }): void {
    this.translate.use(lang.code);
    this.settings.setLanguage(lang.code);
    this.selectedLanguage = lang;
    this.cd.detectChanges();
  }

  /** Records that the user confirmed this modal's action, for the support history. */
  private recordConfirmed(action: string): void {
    this.onboardingModalService.recordSupportHistory(
      this.dialogData?.parentRoute,
      "modal_confirmed",
      describeModalConfirmed(action, { momentStep: this.momentStep }),
    );
  }

  onWelcomeFirstTimeOnboarding(): void {
    this.recordConfirmed("the first-time welcome");
    this.onboardingModalService.hasSeenWelcomeFirstTimeOnboarding.set(true);
    this.dialogRef.close(true);
  }

  continueResumeIncompleteStoreSetup(): void {
    this.recordConfirmed("resuming the incomplete store setup");
    this.onboardingModalService.hasSeenResumeIncompleteStoreSetup.set(true);
    this.router.navigate(["/store/stores"]);
    this.dialogRef.close(true);
  }

  continueAfterLinkingStore(): void {
    this.recordConfirmed("the store link");
    this.dialogRef.close(true);
  }

  continueDelegatedWelcome(): void {
    this.recordConfirmed("the shared stores welcome");
    // On failure the profile service already toasts; keep the modal open so the user can retry.
    this.onboardingModalService
      .completeOnboarding(this.dialogData?.parentRoute, "shared stores welcome")
      .subscribe((profile) => {
        if (profile) {
          this.dialogRef.close(true);
        }
      });
  }

  continueAfterCompleteDeliveryAndThresholdForm(): void {
    this.recordConfirmed("continuing to candidate claims");
    this.onboardingModalService.hasSeenAfterCompleteDeliveryAndThresholdForm.set(
      true,
    );
    this.dialogRef.close(true);

    const storeId = getOnboardingStore(this.storeService.stores())?.storeId;
    if (!storeId) {
      this.router.navigate(["/initial-setup/candidate-claims-found"]);
      return;
    }

    // Best-effort prefetch so candidate-claims-found doesn't have to make its
    // own GET right after this navigation — the backend already started the
    // potential-cases job itself when the store got linked.
    this.onboardingModalService
      .prefetchOnboardingPotentialCases(storeId)
      .pipe(catchError(() => of(null)))
      .subscribe(() => {
        this.router.navigate(["/initial-setup/candidate-claims-found"]);
      });
  }
}
